import React, { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { Order } from '../../interfaces/Table';
import { CollectionResponse } from '../../interfaces/api/api';
import EntityTable, { TableColumn } from './EntityTable';
import { RecoilState, useRecoilState } from 'recoil';
import { LabelDisplayedRowsArgs } from '@material-ui/core';

type FetchEntitiesCallbackFunction<Entity> = (
  page: number,
  limit: number,
  orderBy: keyof Entity,
  order: Order
) => Promise<CollectionResponse<Entity>>;

interface Props<Entity extends { id: string | number }> {
  atomState: RecoilState<{ [key: string]: Entity }>;
  defaultOrderBy?: keyof Entity;
  defaultOrder?: Order;
  defaultLimit?: number;
  hidden?: boolean;
  actions?: (entity: Entity) => ReactElement;
  labelEmptyList?: string;
  labelRowsPerPage?: string;
  columns: TableColumn<Entity>[];
  fetchEntitiesCallback: FetchEntitiesCallbackFunction<Entity>;
  labelDisplayedRows?: (paginationInfo: LabelDisplayedRowsArgs) => React.ReactNode;
}

const EntityTableContainer = <Entity extends { id: string | number }>(props: PropsWithChildren<Props<Entity>>) => {
  const { atomState, defaultOrder, defaultLimit, defaultOrderBy, fetchEntitiesCallback, ...otherProps } = props;

  const [limit, setLimit] = useState(defaultLimit || 50);
  const [orderBy, setOrderBy] = useState<keyof Entity>(defaultOrderBy || 'id');
  const [order, setOrder] = useState<Order>(defaultOrder || 'desc');
  const [page, setPage] = useState(0);

  const { total, loading, error, entities } = useEntities(
    fetchEntitiesCallback,
    atomState,
    page,
    limit,
    orderBy,
    order
  );

  const handleChangeLimit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const limit = parseInt(event.target.value, 10);
    setLimit(limit);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (newOrderBy: keyof Entity) => {
    const isAsc = orderBy === newOrderBy && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(newOrderBy);
    setPage(0);
  };

  return (
    <EntityTable
      loading={loading}
      total={total}
      page={page}
      orderBy={orderBy}
      limit={limit}
      order={order}
      error={error}
      entities={Object.values(entities)}
      onSort={handleSort}
      onChangeLimit={handleChangeLimit}
      onChangePage={handleChangePage}
      {...otherProps}
    />
  );
};

const useEntities = <Entity extends { id: string | number }>(
  fetchEntitiesCallback: FetchEntitiesCallbackFunction<Entity>,
  atomState: RecoilState<{ [key: string]: Entity }>,
  page: number,
  limit: number,
  orderBy: keyof Entity,
  order: Order
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [entities, setEntities] = useRecoilState(atomState);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchEntitiesCallback(page + 1, limit, orderBy, order)
      .then((collection) => {
        const items = Object.assign({}, ...collection.items.map((item) => ({ [item.id]: item })));

        setEntities(items);
        setLoading(false);
        setTotal(collection.total);
      })
      .catch((e) => {
        setLoading(false);
        setError(e.message);
      });
  }, [fetchEntitiesCallback, page, limit, order, orderBy]);

  return { total, loading, error, entities };
};

export default EntityTableContainer;
